import { useRef } from "react";

import { FormAIFillSettings, StaffDetails } from "@smart/bridge-types-basic";
import { mapEntries } from "@smart/itops-utils-basic";

import { AutofillUnavailableModal } from "./autofill-components";
import { FieldComponentOverrideProps, FieldProps } from "./field-components";
import {
  IntakeFormAutoFill,
  IntakeFormSection,
  IntakeFormSummary,
} from "./form-components";
import { Heading } from "./heading";
import { ScrollWrapper } from "./section-components";
import { TeamLabel } from "./shared-components/team-label";
import { IntakeSidebar } from "./sidebar";
import { IntakeStatus } from "./status";
import { IntakeFormHookProps, useIntakeForm } from "../hooks";
import {
  FieldItem,
  IntakeFormSectionRef,
  LookupOptions,
  SectionItem,
  TeamItem,
  Toast,
} from "../types";

export type IntakeFormProps = IntakeFormHookProps & {
  isMatterClosed?: boolean;
  form: {
    team: TeamItem;
    hideTeam?: boolean;
    response?: string;
    category?: string;
    aiFillSettings?: FormAIFillSettings;
    title?: string;
  };
  lookup: LookupOptions;
  previewOptions?: {
    previewErrorToggle?: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
    previewResponses?: [
      Record<string, any>,
      React.Dispatch<React.SetStateAction<Record<string, any>>>,
    ];
    previewLastSection: [
      SectionItem | "summary" | undefined,
      React.Dispatch<React.SetStateAction<SectionItem | "summary" | undefined>>,
    ];
  };
  isInternal?: boolean;
  newUI?: boolean;
  staffDetails: StaffDetails[];
  showConnectionBanner?: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ];
  intakeFormKey?: string;
  isPreview?: boolean;
  showPaymentPlaceholder?: boolean;
  loading?: boolean;
  isAutoFillFileUploadDisabled?: boolean;
  fieldComponentOverride?: (
    fieldProps: FieldProps & FieldComponentOverrideProps,
  ) => React.ReactNode | undefined;
  setToasts?: (toasts: Toast[]) => void;
  setFields?: (setFn: (fields: FieldItem[]) => FieldItem[]) => void;
};

export const IntakeForm = ({
  isMatterClosed,
  intakeFormKey,
  form,
  lookup,
  previewOptions,
  isInternal,
  newUI,
  staffDetails,
  showConnectionBanner,
  loading,
  isAutoFillFileUploadDisabled,
  fieldComponentOverride,
  setToasts,
  setFields,
  showPaymentPlaceholder,
  ...formOptions
}: IntakeFormProps) => {
  const {
    summary,
    statusProps,
    sidebarProps,
    sectionProps,
    summaryProps,
    printProps,
    uploadFileProps,
    autoFillProps,
    autoFillUnavailableProps,
  } = useIntakeForm({
    ...formOptions,
    aiFillSettings: form.aiFillSettings,
    previewLastSection: previewOptions?.previewLastSection,
    submission: {
      ...formOptions.submission,
      responses:
        (previewOptions?.previewResponses?.[0]
          ? mapEntries<Record<string, any>, Record<string, { value: any }>>(
              previewOptions.previewResponses[0],
              (value) => ({
                value,
              }),
            )
          : undefined) || formOptions.submission.responses,
    },
    isPreview: formOptions.isPreview,
  });

  const sectionRef = useRef<IntakeFormSectionRef>(null);
  const shouldHideTeamInfo = form.hideTeam || formOptions.isPreview;

  if (
    isMatterClosed ||
    (statusProps.status !== "active" && !isInternal) ||
    statusProps.deleted
  ) {
    return (
      <ScrollWrapper>
        {!shouldHideTeamInfo && (
          <Heading
            variant="intake"
            left={
              <TeamLabel
                teamName={form.team.name}
                teamPicture={form.team.picture}
              />
            }
          />
        )}
        <IntakeStatus
          {...form}
          {...statusProps}
          printOptions={{
            ...printProps,
            content: {
              ...printProps.content,
              formTitle: form.title,
              teamDetails: form.team,
              completedOn: formOptions.submission.updatedAt,
            },
          }}
          isMatterClosed={isMatterClosed}
        />
      </ScrollWrapper>
    );
  }

  const aiFillFromMatterInfo = !!formOptions.submission.aiFillFromMatterInfo;
  const pageDisabled =
    formOptions.autoFillStatus === "filling" ||
    (aiFillFromMatterInfo && loading);

  const renderContent = () => {
    if (summary) {
      return (
        <IntakeFormSummary
          {...form}
          {...summaryProps}
          {...sectionProps}
          previewOptions={
            previewOptions?.previewResponses &&
            previewOptions?.previewErrorToggle
              ? {
                  previewResponses: previewOptions.previewResponses[0],
                  clearLastPreviewSection: () =>
                    previewOptions.previewLastSection[1](undefined),
                  previewErrorToggle: previewOptions.previewErrorToggle,
                }
              : undefined
          }
          isPreview={formOptions.isPreview}
          isTeamHidden={shouldHideTeamInfo}
          setToasts={setToasts}
        />
      );
    }

    if (autoFillProps.isAutoFillSectionSelected) {
      return (
        <IntakeFormAutoFill
          {...form}
          {...autoFillProps}
          uploadFileProps={uploadFileProps}
          fileLookup={lookup.fileLookup}
          isFileUploadDisabled={isAutoFillFileUploadDisabled}
          isTeamHidden={shouldHideTeamInfo}
        />
      );
    }

    return (
      <IntakeFormSection
        {...form}
        {...lookup}
        {...sectionProps}
        uploadFileProps={uploadFileProps}
        ref={sectionRef}
        submissionSyncStatus={formOptions.submission.syncStatus}
        previewOptions={previewOptions}
        staffDetails={staffDetails}
        showConnectionBanner={showConnectionBanner}
        loadResponses={formOptions.loadResponses}
        aiFillFromMatterInfo={aiFillFromMatterInfo}
        loading={loading}
        isPreview={formOptions.isPreview}
        showPaymentPlaceholder={showPaymentPlaceholder}
        fieldComponentOverride={fieldComponentOverride}
        setToasts={setToasts}
        setFields={setFields}
        isTeamHidden={shouldHideTeamInfo}
        isWebEnquiry={formOptions.submission.communicationMethod === "embed"}
      />
    );
  };

  return (
    <IntakeSidebar
      {...sidebarProps}
      sectionRef={sectionRef}
      newUI={newUI}
      isPreview={formOptions.isPreview}
      isTeamHidden={shouldHideTeamInfo}
    >
      {!shouldHideTeamInfo && (
        <Heading
          variant="intake"
          left={
            <TeamLabel
              teamName={form.team.name}
              teamPicture={form.team.picture}
            />
          }
        />
      )}
      {pageDisabled && (
        <div className="absolute h-full w-full z-80 m-0 p-0 border-0 bg-neutral-200 opacity-40" />
      )}
      {renderContent()}
      <AutofillUnavailableModal {...autoFillUnavailableProps} />
    </IntakeSidebar>
  );
};
